import { computed } from "vue";
import { watchDebounced } from "@vueuse/core";
import { Currency, LineType, OrderSessionState } from "@smeetz/orders";
import { useOrderStore } from "@smeetz/order-store";
import i18n from "@/lang";
// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;
import { LogService } from "@/services/logging";
import CashFloatModule from "@/store/modules/cash-float";
import CommonModule from "@/store/modules/common";
import CustomersModule from "@/store/modules/customers";
import DeviceModule from "@/store/modules/device";
import POSConfigModule from "@/store/modules/pos-config";
import { useOrderMetaStore } from "@/stores/order-meta.store";
import { useGroupOffersStore } from "@/stores/group-offers.store";
import { getTimeslotById } from "@/dexie/timeslots";
import { formatDateTime } from "@/utils/helpers/dates";
import { isOrderEdited } from "./orders.helper";
import {
  getDisplayedPayments,
  getLineStatusTranslation,
  getOrderLineDiscountLines,
  getOrderLineDiscountedTotal,
  getOrderLineDisplayName,
  getPaymentCurrencies,
  getPendingVoucherRows,
  shouldShowTicketInfo,
} from "./order-summary-display";
import { PaymentMethodToTranslatedString } from "./pos-config";
import person from "@/assets/images/svg/person.svg";

/**
 * The customer display is a second, read-only window (`/customer-display`)
 * that boots with `customerDisplaySetup()` — no Pinia order store, no order
 * services. Everything it renders is computed here in the MAIN window and
 * shipped over the `pos.smeetz` BroadcastChannel as a plain serializable
 * payload.
 */

export interface CustomerBasketLine {
  id: string;
  name: string;
  priceName?: string;
  timeslot?: string;
  seatsLabel?: string;
  discountLines: string[];
  quantity: number;
  unitPrice: string;
  totalPrice: string;
  /** Line total after per-line discounts; set only when discount lines exist. */
  discountedTotal?: string;
  isCancelled: boolean;
  /** Confirmed/cancelled tickets/sets label; set only on completed lines. */
  statusLabel?: string;
}

export interface CustomerBasketData {
  isLongTitle: boolean;
  iconContent: string;
  iconAlt: string;
  iconStyle: string;
  customerFullName: string;
  currency: string;
  lines: CustomerBasketLine[];
  showDiscountRow: boolean;
  totalDiscounts: string;
  showTotals: boolean;
  subtotal: string;
  totalVat: string;
  showTotal: boolean;
  total: string;
  multiCurrencyTotals: {
    label: string;
    exchangeRate: number;
    amount: string;
  }[];
  vouchers: {
    id: string;
    label: string;
    showPendingHint: boolean;
    amount: string;
    currency: string;
  }[];
  payments: {
    id: string;
    methodLabel: string;
    currencyLabel: string;
    amount: string;
  }[];
  showConfirmedAmounts: boolean;
  totalConfirmedAmount: string;
  showRefundedAmount: boolean;
  refundedAmount: string;
  balance: string;
}

// One channel per window; a BroadcastChannel never receives its own posts,
// so the main window can post and listen on the same instance.
let broadcastChannel: BroadcastChannel | null = null;

function getBroadcastChannel(): BroadcastChannel {
  if (!broadcastChannel) {
    broadcastChannel = new BroadcastChannel("pos.smeetz");
  }
  return broadcastChannel;
}

/**
 * Default currency of the group's cash float, used to skip the base currency
 * in the extra multi-currency total rows.
 */
function getGroupCurrencyLabel(): string | undefined {
  const startAmounts = CashFloatModule.cashFloat?.startAmounts;
  const available = [];
  for (const currency in startAmounts) {
    const currencyInfo = POSConfigModule.groupCurrencies.find(
      (c) => c.label === currency
    );
    if (currencyInfo) {
      available.push(currencyInfo);
    }
  }
  return (available.find((c) => c.isDefault === 1) || available[0])?.label;
}

/**
 * Build the customer display payload from the selected order's summary.
 * Rows, labels and amounts mirror AsideOrderSummary; dynamic translated
 * strings are precomputed here so the display stays a dumb renderer.
 */
export async function getCustomerBasketData(): Promise<CustomerBasketData> {
  const orderStore = useOrderStore();
  const orderMetaStore = useOrderMetaStore();
  const groupOffers = useGroupOffersStore().offers;
  const summary = orderStore.selectedOrderSummary;

  const currency = String(summary.currency || Currency.CHF);
  const isEdited = isOrderEdited.value;
  const { isInPaymentStep, isInCompletedStep } = orderMetaStore;

  const lines: CustomerBasketLine[] = [];
  for (const orderLine of summary.orderLines) {
    const showTicketInfo = shouldShowTicketInfo(orderLine);

    let timeslot: string | undefined;
    if (showTicketInfo && orderLine.timeSlotId) {
      const timeslotRecord = await getTimeslotById(orderLine.timeSlotId);
      if (timeslotRecord) {
        timeslot = formatDateTime(timeslotRecord.startDateTime);
      }
    }

    const seats = orderLine.seats ?? [];
    const seatsLabel = seats.length
      ? `${t("field.seat")}: ${seats.map((s) => s.seatLabel).join(", ")}`
      : undefined;

    const discountLines = getOrderLineDiscountLines(
      orderLine,
      summary,
      groupOffers,
      t
    );
    const isCancelled = orderLine.lineType === LineType.CancelLine;

    lines.push({
      id: orderLine.id,
      name: getOrderLineDisplayName(orderLine),
      priceName: showTicketInfo ? orderLine.priceName : undefined,
      timeslot,
      seatsLabel,
      discountLines,
      quantity: Math.abs(orderLine.quantity),
      unitPrice: (orderLine.overridePrice ?? orderLine.unitPrice).toFixed(2),
      totalPrice: orderLine.totalPrice.toFixed(2),
      discountedTotal:
        discountLines.length && !isCancelled
          ? getOrderLineDiscountedTotal(orderLine).toFixed(2)
          : undefined,
      isCancelled,
      statusLabel:
        orderLine.state === OrderSessionState.COMPLETED
          ? getLineStatusTranslation(orderLine, t)
          : undefined,
    });
  }

  const payments = getDisplayedPayments(summary).map((payment) => ({
    id: String(payment.id),
    methodLabel: PaymentMethodToTranslatedString.value[payment.paymentMethod],
    currencyLabel: payment.multiCurrencyInfo?.paymentCurrency || currency,
    amount: (
      (payment.multiCurrencyInfo?.providedCashBill || payment.value) / 100
    ).toFixed(2),
  }));

  const groupCurrencyLabel = getGroupCurrencyLabel();
  const showMultiCurrencyTotals =
    (isInPaymentStep || isInCompletedStep) &&
    !isEdited &&
    summary.payments.length > 0;
  const multiCurrencyTotals = showMultiCurrencyTotals
    ? getPaymentCurrencies(summary)
        .filter((c) => c.label !== groupCurrencyLabel)
        .map((c) => ({
          label: c.label,
          exchangeRate: c.exchangeRate,
          amount: (summary.totalWithDiscount * c.exchangeRate).toFixed(2),
        }))
    : [];

  const vouchers = getPendingVoucherRows(summary).map((voucher) => ({
    ...voucher,
    id: String(voucher.id),
    amount: voucher.amount.toFixed(2),
    currency: String(voucher.currency),
  }));

  const customer = CustomersModule.selectedCustomers.find(
    (c: any) => c.orderId === orderStore.selectedOrder?.id
  );

  return {
    isLongTitle: String(t("asideordersummary.title")).length > 15,
    iconContent: person,
    iconAlt: "user",
    iconStyle:
      "width: 20px; height: 20px; filter: brightness(0) saturate(100%) contrast(170%) invert(100%);",
    customerFullName: customer?.fullName || "",
    currency,
    lines,
    showDiscountRow: isInPaymentStep && !!summary.totalDiscounts,
    totalDiscounts: summary.totalDiscounts.toFixed(2),
    showTotals: isInPaymentStep || isInCompletedStep,
    subtotal: summary.subtotal.toFixed(2),
    totalVat: summary.totalVat.toFixed(2),
    showTotal: isEdited || isInPaymentStep || isInCompletedStep,
    total: summary.totalWithDiscount.toFixed(2),
    multiCurrencyTotals,
    vouchers,
    payments,
    showConfirmedAmounts: payments.length > 0 || vouchers.length > 0,
    totalConfirmedAmount: (
      summary.totalPayments / 100 +
      summary.totalVouchers
    ).toFixed(2),
    showRefundedAmount:
      isEdited || (isInCompletedStep && !!summary.totalRefunds),
    // totalRefunds sums negative payment values; display it positive.
    refundedAmount: Math.abs(summary.totalRefunds / 100).toFixed(2),
    balance: summary.balance.toFixed(2),
  };
}

/**
 * Build and broadcast the current basket payload to the customer display.
 */
export async function updateCustomerScreen() {
  try {
    const customerBasketData = await getCustomerBasketData();
    getBroadcastChannel().postMessage(
      JSON.stringify({
        message: "Order has been updated",
        isOrderUpdated: true,
        customerBasketData,
        isChartToUpdate: false,
      })
    );
  } catch (error) {
    LogService.info("Error broadcasting basket to the customer display", {
      error,
    });
  }
}

/**
 * Main-window side of the customer display sync. Rebroadcasts the basket
 * whenever the selected order's summary or the order step changes, and
 * answers the snapshot request the display sends on mount (the display
 * window cannot compute the basket itself — its order store is empty).
 */
export function setupCustomerDisplaySync() {
  const orderStore = useOrderStore();
  const orderMetaStore = useOrderMetaStore();

  watchDebounced(
    [() => orderStore.selectedOrderSummary, () => orderMetaStore.step],
    () => {
      if (!CommonModule.isCustomerScreenOpened) {
        return;
      }
      updateCustomerScreen();
    },
    { deep: true, debounce: 200, maxWait: 1000 }
  );

  getBroadcastChannel().onmessage = (event: MessageEvent) => {
    try {
      const receivedData = JSON.parse(event.data);
      if (receivedData?.requestBasket) {
        updateCustomerScreen();
      }
    } catch {
      // Ignore malformed channel messages
    }
  };
}

/**
 * Called whenever the user renders a seating chart
 * This function will render the same chart for the customer in Customer Display
 */
export async function renderChartInCustomerDisplay(renderedChartData: any) {
  LogService.info("Going to render attached chart in CustomerDisplay", {
    params: renderedChartData,
  });
  getBroadcastChannel().postMessage(
    JSON.stringify({
      message: "User rendered attached chart",
      isOrderUpdated: false,
      isChartToUpdate: true,
      chartData: renderedChartData,
      renderCustomerSeatingChart: true,
    })
  );
}

/**
 * Called whenever the user closes a seating chart
 * This function will close the same chart in Customer Display
 */
export async function closeChartInCustomerDisplay(renderedChartData: any) {
  LogService.info("Going to close attached chart in CustomerDisplay", {
    params: renderedChartData,
  });
  getBroadcastChannel().postMessage(
    JSON.stringify({
      message: "User closed attached chart",
      isOrderUpdated: false,
      isChartToUpdate: true,
      renderedChartData,
      renderCustomerSeatingChart: false,
      closeRenderedCustomerSeatingChart: true,
    })
  );
}

/**
 * This called after a user selects seats in the main screen, it will show the selected seats in the customer display as well
 */
export async function selectSeatsInCustomerDisplay(
  newlySelectedSeats: string[]
) {
  LogService.info("Going to select seats in chart in CustomerDisplay", {
    params: newlySelectedSeats,
  });
  getBroadcastChannel().postMessage(
    JSON.stringify({
      message: "User selected seats in chart",
      isChartToUpdate: false, // we don't close the chart, nor we open it again (it is already opened and we keep it)
      newlySelectedSeats,
    })
  );
}

/**
 * This called after a user deselects seats in the main screen, it will deselect those seats in the customer display as well
 */
export async function deselectSeatsInCustomerDisplay(
  deselectedSeats: string[]
) {
  LogService.info("Going to deselect seats in chart in CustomerDisplay", {
    params: deselectedSeats,
  });
  getBroadcastChannel().postMessage(
    JSON.stringify({
      message: "User deselected seats in chart",
      isChartToUpdate: false, // we don't close the chart, nor we open it again (it is already opened and we keep it)
      deselectedSeats,
    })
  );
}

/**
 * Open a new Electron window displaying CustomerDisplay component
 */
export function openCustomerDisplay() {
  const electronApi = window?.electronAPI;
  if (electronApi) {
    // Send a message to Electron to open a new window
    LogService.info("going to open customer facing display");
    electronApi.openNewWindow(`${window.location.origin}/customer-display`);
    CommonModule.setIsCustomerDisplayOpened(true);
    LogService.info("customer facing display opened");
  }
}

/**
 * Close the customer display Electron window
 */
export function closeCustomerDisplay() {
  const electronApi = window?.electronAPI;
  if (electronApi) {
    // Send a message to Electron to close customer display
    LogService.info("going to close customer facing display");
    electronApi.closeNewWindow();
    LogService.info("customer facing display closed");
    CommonModule.setIsCustomerDisplayOpened(false);
  }
}

export const hasCustomerDisplay = computed(() => {
  if (
    DeviceModule.hardwareInfo &&
    DeviceModule.hardwareInfo.displays &&
    DeviceModule.hardwareInfo.displays.length > 1
  ) {
    return true;
  }
  return false;
});
